import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  ArrowLeft, AlertTriangle, Shield, Heart, Brain, Activity,
  MessageSquare, Users, Link as LinkIcon, AlertOctagon, Sparkles,
  TrendingUp, Pill, BookOpen, CheckCircle, Mail,
} from 'lucide-react';

const Section = ({ icon: Icon, title, children, accent = 'stone' }) => {
  const accentMap = {
    stone:  'bg-stone-100 text-stone-700',
    rose:   'bg-rose-100 text-rose-700',
    sky:    'bg-sky-100 text-sky-700',
    amber:  'bg-amber-100 text-amber-700',
    emerald:'bg-emerald-100 text-emerald-700',
    violet: 'bg-violet-100 text-violet-700',
    red:    'bg-red-100 text-red-700',
    indigo: 'bg-indigo-100 text-indigo-700',
  };
  return (
    <Card className="p-6 md:p-8 bg-white border-stone-200 shadow-sm">
      <div className="flex items-start gap-4 mb-4">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${accentMap[accent]}`}>
          <Icon className="w-6 h-6" strokeWidth={1.6} />
        </div>
        <h2 className="text-xl md:text-2xl font-nunito font-bold text-[#2D2D2D] mt-2">
          {title}
        </h2>
      </div>
      <div className="text-[#57534E] leading-relaxed space-y-3 text-[15px]">
        {children}
      </div>
    </Card>
  );
};

const Bullets = ({ items }) => (
  <ul className="list-disc list-outside pl-5 space-y-1 marker:text-stone-400">
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

const MedicalDisclosurePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="medical-disclosure-page">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Button
          onClick={() => navigate(-1)}
          variant="ghost"
          className="mb-8 text-primary hover:text-primary/80"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>

        {/* Hero */}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-8 mb-8 border border-amber-200">
          <div className="flex items-center gap-4 mb-4">
            <AlertTriangle className="w-12 h-12 text-amber-600" strokeWidth={1.5} />
            <div>
              <h1 className="text-3xl md:text-4xl font-nunito font-bold text-[#2D2D2D]">
                Medical Disclaimer
              </h1>
              <p className="text-amber-700">Important information about using Calm Pathways</p>
            </div>
          </div>
          <p className="text-amber-800 leading-relaxed">
            Please read this Medical Disclaimer carefully before using Calm Pathways. This information
            is important for understanding the intended purpose, scope, and limitations of the application.
          </p>
        </div>

        <div className="space-y-6">
          <Section icon={Heart} title="Medical & Educational Disclaimer" accent="rose">
            <p>
              Calm Pathways (“the App”) is an educational, organizational, and wellness-support tool
              designed for parents, guardians, and caregivers navigating complex neuroimmune-related
              experiences, including but not limited to PANS, PANDAS, autoimmune-related behavioral
              changes, histamine-related concerns, mast cell activation, MTHFR-related discussions,
              infections, environmental factors, and other commonly discussed wellness topics.
            </p>
            <p>
              The App is intended solely for informational, educational, and organizational purposes.
            </p>
            <p>
              The App does not provide medical advice, diagnosis, treatment, therapy, clinical
              decision-making, psychiatric services, or emergency intervention.
            </p>
            <p>Use of the App does not create:</p>
            <Bullets items={[
              'a provider-patient relationship,',
              'therapist-patient relationship,',
              'physician-patient relationship,',
              'clinical treatment relationship,',
              'or any other healthcare relationship.',
            ]} />
            <p>
              Always consult qualified healthcare professionals regarding medical concerns, diagnoses,
              medications, supplements, treatment plans, or changes in symptoms or behavior.
            </p>
          </Section>

          <Section icon={Users} title="Parent & Caregiver Use Only" accent="sky">
            <p>
              Calm Pathways is intended for use by parents, legal guardians, caregivers, and authorized adults.
            </p>
            <p className="font-semibold text-[#2D2D2D]">
              The App is not intended for direct independent use by children.
            </p>
            <p>Parents and caregivers are responsible for:</p>
            <Bullets items={[
              'information entered into the App,',
              'interpretation of logged information,',
              'supervision of any child-related care decisions,',
              'and consultation with qualified healthcare professionals.',
            ]} />
          </Section>

          <Section icon={BookOpen} title="Educational Use Only" accent="indigo">
            <p>
              All content within the App — including educational materials, symptom tracking tools, logs,
              charts, AI-generated summaries, exports, wellness information, supplement references, food
              logs, environmental observations, community content, and organizational features — is provided
              for educational and informational purposes only.
            </p>
            <p>Nothing within the App should be interpreted as:</p>
            <Bullets items={[
              'medical advice,',
              'psychiatric advice,',
              'individualized healthcare recommendations,',
              'diagnosis,',
              'treatment guidance,',
              'medication recommendations,',
              'supplement prescriptions,',
              'or emergency instructions.',
            ]} />
            <p>
              Medical decisions should always be made in consultation with licensed healthcare professionals
              familiar with the child’s individual medical history and circumstances.
            </p>
          </Section>

          <Section icon={Sparkles} title="AI-Generated Content Disclaimer" accent="violet">
            <p>
              Certain features within Calm Pathways may utilize artificial intelligence (“AI”) to generate
              educational summaries, organizational insights, informational responses, trends, correlations,
              or reports.
            </p>
            <p>AI-generated content:</p>
            <Bullets items={[
              'may be incomplete, inaccurate, outdated, biased, or inappropriate;',
              'is generated automatically;',
              'is not reviewed by licensed healthcare professionals;',
              'and should never be relied upon for diagnosis, treatment decisions, medication changes, supplement decisions, or emergency situations.',
            ]} />
            <p>
              Parents and caregivers are responsible for independently reviewing and verifying important
              information with qualified healthcare professionals.
            </p>
          </Section>

          <Section icon={TrendingUp} title="Tracking, Correlations & Pattern Data" accent="emerald">
            <p>The App may allow users to track and organize:</p>
            <Bullets items={[
              'symptoms,', 'behaviors,', 'emotional states,', 'foods,', 'supplements,',
              'medications,', 'environmental factors,', 'sleep,', 'infections,',
              'routines,', 'and other observations.',
            ]} />
            <p>
              The App may also generate trends, correlations, summaries, visualizations, or organizational
              insights based on entered information.
            </p>
            <p>
              These features are intended solely to support organization, reflection, and discussion with
              healthcare professionals.
            </p>
            <p>Any trends, correlations, or insights displayed within the App:</p>
            <Bullets items={[
              'do not establish medical causation,',
              'do not constitute diagnosis,',
              'do not confirm triggers,',
              'and should not be interpreted as treatment recommendations or clinical conclusions.',
            ]} />
            <p className="font-semibold text-[#2D2D2D]">
              Correlation does not establish causation.
            </p>
            <p>
              Insights generated by the App depend on information entered by users and may be incomplete,
              inaccurate, or misleading if entered information is incomplete or incorrect.
            </p>
          </Section>

          <Section icon={Pill} title="Supplement, Nutrition & Wellness Information" accent="amber">
            <p>Any information related to:</p>
            <Bullets items={[
              'supplements,', 'nutrition,', 'foods,', 'environmental exposures,',
              'wellness practices,', 'dosing ranges,', 'weight-based calculations,',
              'or commonly discussed protocols',
            ]} />
            <p>is provided solely for general educational context.</p>
            <p>Such information:</p>
            <Bullets items={[
              'is not individualized,',
              'may not be appropriate for every child,',
              'and should not be interpreted as medical recommendations, prescriptions, or treatment instructions.',
            ]} />
            <p>Always consult a licensed healthcare provider before:</p>
            <Bullets items={['starting,', 'stopping,', 'combining,', 'or modifying']} />
            <p>any medication, supplement, nutrition plan, or wellness-related intervention.</p>
            <p>
              Calm Pathways does not guarantee the safety, effectiveness, or appropriateness of any supplement,
              protocol, intervention, or wellness approach discussed within the App.
            </p>
          </Section>

          <Section icon={Brain} title="Evolving Research & Clinical Perspectives" accent="indigo">
            <p>Certain conditions, symptoms, and wellness topics discussed within the App involve:</p>
            <Bullets items={[
              'evolving scientific research,',
              'differing medical opinions,',
              'ongoing clinical debate,',
              'and emerging areas of study.',
            ]} />
            <p>
              Educational content within the App may reference theories, hypotheses, clinical observations,
              or commonly discussed perspectives that are not universally accepted within the medical community.
            </p>
            <p>
              The inclusion of such information does not constitute endorsement, validation, or proof of effectiveness.
            </p>
          </Section>

          <Section icon={AlertOctagon} title="No Emergency or Crisis Support" accent="red">
            <p className="font-bold text-red-700 uppercase tracking-wide">
              The App is not monitored for emergencies.
            </p>
            <p>Calm Pathways does not provide:</p>
            <Bullets items={[
              'crisis intervention,',
              'suicide prevention services,',
              'emergency monitoring,',
              'urgent medical review,',
              'or real-time healthcare supervision.',
            ]} />
            <p>
              Entries, logs, messages, uploaded information, reports, AI interactions, or community posts
              are not continuously monitored for emergencies, safety concerns, or crisis situations.
            </p>
            <p>The App should never be used in place of:</p>
            <Bullets items={[
              'emergency services,',
              'crisis care,',
              'emergency psychiatric support,',
              'or urgent medical evaluation.',
            ]} />
            <p>
              If a child may be at risk of harm to self or others, or is experiencing a medical or psychiatric
              emergency, immediately contact:
            </p>
            <Bullets items={[
              '911 or local emergency services,',
              'the child’s physician,',
              'emergency medical personnel,',
              'or an appropriate crisis service.',
            ]} />
          </Section>

          <Section icon={MessageSquare} title="Messaging & Communication Features" accent="sky">
            <p>
              If messaging, communication, support, or educational interaction features are available within
              the App, such features are provided solely for informational and educational support purposes.
            </p>
            <p>Messaging features:</p>
            <Bullets items={[
              'do not constitute medical care,',
              'are not monitored continuously,',
              'and should never be used for emergencies or urgent medical concerns.',
            ]} />
            <p>Response times may vary and are not guaranteed.</p>
          </Section>

          <Section icon={Users} title="Community Content Disclaimer" accent="emerald">
            <p>
              If the App includes community, forum, or peer discussion features, content shared by users
              reflects individual experiences and opinions only.
            </p>
            <p>User-generated content:</p>
            <Bullets items={[
              'is not medical advice,',
              'is not reviewed for clinical accuracy,',
              'and should not be relied upon for treatment decisions.',
            ]} />
            <p>
              Calm Pathways does not verify, endorse, or guarantee the accuracy of statements made by users
              within community features.
            </p>
            <p>
              Parents and caregivers should independently evaluate information shared by other users and
              consult healthcare professionals regarding medical decisions.
            </p>
          </Section>

          <Section icon={LinkIcon} title="External Links & Third-Party Resources" accent="violet">
            <p>
              The App may include links to external websites, resources, products, articles, services, or
              third-party content for informational convenience.
            </p>
            <p>Calm Pathways does not control or guarantee:</p>
            <Bullets items={[
              'the accuracy,', 'reliability,', 'availability,', 'security,', 'or practices',
            ]} />
            <p>of third-party resources.</p>
            <p>Accessing third-party content is done at the user’s own discretion and risk.</p>
          </Section>

          <Section icon={Activity} title="No Guaranteed Outcomes" accent="amber">
            <p>Calm Pathways does not guarantee:</p>
            <Bullets items={[
              'symptom improvement,',
              'behavioral improvement,',
              'treatment success,',
              'flare prevention,',
              'identification of triggers,',
              'or any specific medical or wellness outcome.',
            ]} />
            <p>
              Use of the App and reliance on any information provided is at the user’s sole discretion and risk.
            </p>
          </Section>

          <Section icon={CheckCircle} title="Acknowledgment" accent="emerald">
            <p>By using Calm Pathways, you acknowledge that:</p>
            <Bullets items={[
              'you have read and understood this Medical Disclaimer;',
              'you understand the App is intended solely as an educational and organizational tool;',
              'the App does not replace professional medical care;',
              'and you agree to use the App at your own discretion and risk.',
            ]} />
            <p>
              You further acknowledge that all healthcare decisions should be made in consultation with
              qualified healthcare professionals.
            </p>
          </Section>

          <Section icon={Mail} title="Questions?" accent="stone">
            <p>If you have questions regarding this Medical Disclaimer, please contact:</p>
            <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 mt-2">
              <p className="font-semibold text-[#2D2D2D]">Me and My PANDAS LLC</p>
              <p>
                Email:{' '}
                <a
                  href="mailto:contact@meandmypandas.com"
                  className="text-primary hover:underline font-medium"
                >
                  contact@meandmypandas.com
                </a>
              </p>
            </div>
          </Section>
        </div>

        <div className="mt-10 p-6 bg-stone-50 border border-stone-200 rounded-xl text-center">
          <Shield className="w-8 h-8 text-stone-500 mx-auto mb-2" />
          <p className="text-sm text-[#57534E]">
            Last updated: May 2026 · By using Calm Pathways, you acknowledge that you have read,
            understood, and agree to this Medical Disclaimer.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MedicalDisclosurePage;
